import { useEffect } from "react";

interface SeoMetaProps {
	title: string;
	description: string;
	canonicalPath?: string;
	jsonLd?: Record<string, unknown> | Record<string, unknown>[];
	imagePath?: string;
}

function upsertMetaByName(name: string, content: string) {
	let meta = document.querySelector(
		`meta[name="${name}"]`,
	) as HTMLMetaElement | null;

	if (!meta) {
		meta = document.createElement("meta");
		meta.setAttribute("name", name);
		document.head.appendChild(meta);
	}

	meta.setAttribute("content", content);
}

function upsertMetaByProperty(property: string, content: string) {
	let meta = document.querySelector(
		`meta[property="${property}"]`,
	) as HTMLMetaElement | null;

	if (!meta) {
		meta = document.createElement("meta");
		meta.setAttribute("property", property);
		document.head.appendChild(meta);
	}

	meta.setAttribute("content", content);
}

function upsertCanonical(pathname: string) {
	const canonicalUrl = `${window.location.origin}${pathname}`;
	let canonical = document.querySelector(
		"link[rel='canonical']",
	) as HTMLLinkElement | null;

	if (!canonical) {
		canonical = document.createElement("link");
		canonical.setAttribute("rel", "canonical");
		document.head.appendChild(canonical);
	}

	canonical.setAttribute("href", canonicalUrl);
}

function upsertJsonLd(data: SeoMetaProps["jsonLd"]) {
	const scriptId = "seo-json-ld";
	const script = document.getElementById(scriptId) as HTMLScriptElement | null;

	if (!data) {
		if (script) {
			script.remove();
		}
		return;
	}

	let nextScript = script;

	if (!nextScript) {
		nextScript = document.createElement("script");
		nextScript.id = scriptId;
		nextScript.type = "application/ld+json";
		document.head.appendChild(nextScript);
	}

	nextScript.text = JSON.stringify(data);
}

export default function SeoMeta({
	title,
	description,
	canonicalPath = "/",
	jsonLd,
	imagePath = "/logo/logo.png",
}: SeoMetaProps) {
	useEffect(() => {
		const canonicalUrl = `${window.location.origin}${canonicalPath}`;
		const imageUrl = `${window.location.origin}${imagePath}`;

		document.title = title;
		upsertMetaByName("description", description);
		upsertMetaByProperty("og:title", title);
		upsertMetaByProperty("og:description", description);
		upsertMetaByProperty("og:type", "website");
		upsertMetaByProperty("og:url", canonicalUrl);
		upsertMetaByProperty("og:image", imageUrl);
		upsertMetaByName("twitter:card", "summary_large_image");
		upsertMetaByName("twitter:title", title);
		upsertMetaByName("twitter:description", description);
		upsertMetaByName("twitter:image", imageUrl);
		upsertCanonical(canonicalPath);
		upsertJsonLd(jsonLd);
	}, [title, description, canonicalPath, jsonLd, imagePath]);

	return null;
}
